import { Box, Grid } from '@mui/material';
import _ from 'lodash';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { routeName } from 'routes/routes-name';

/** TRANSLATION*/
import { useTranslation } from 'react-i18next';

/** API */
import ClinicApi from 'api/master/clinic.api';
import ReportApi, { FindAllReportInterface } from 'api/report.api';
import { getBranch } from 'utils/app.utils';

/** COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard';
import InputSecondNewDatePicker, { PICKER_VIEWS } from 'component/Input/InputSecondNewDatePicker';
import Loading from 'component/Loading';

/** STYLE */
import { notiError } from 'component/notifications/notifications';
import FunctionPrint from 'component/Print/print';
import * as UseStyled from 'features/report/useStyled';
import CheckboxButton from 'new-components/buttons/checkbox-button';
import MainButton from 'new-components/buttons/main-button';
import SelectInput from 'new-components/inputs/select-input';
import { ExportToExcel } from './exportToExcel';
import RemittanceList from './remittanceList';

interface SummaryRow {
    label: string;
    amount: number;
    fullAmount?: number | string;
    color?: string;        // ex: '#1976d2', 'green', 'red'
    fontWeight?: number;   // ex: 700
    alignLabel?: 'left' | 'center' | 'right';
}

interface Props {
    branchName: string;
    date: string;
    rows: SummaryRow[];
}


const thStyle: React.CSSProperties = {
    border: '1px solid black',
    padding: '8px 4px',
    backgroundColor: '#f5f5f5',
    fontWeight: 700,
    textAlign: 'left',
};
const tdStyleBase: React.CSSProperties = {
    border: '1px solid black',
    padding: '8px 4px',
    fontWeight: 500,
};

export default function Remittance() {
    const { t } = useTranslation();
    const history = useHistory();
    const componentRef = useRef<HTMLDivElement>(null);
    const momentNow = moment().format('YYYY-MM-DD');

    const [branchId, setBranchId] = useState<any>(getBranch());
    const [branches, setBranches] = useState<any[]>([]);
    const [rowCount, setRowCount] = useState<number>(0);
    const [sortBy, setSortBy] = useState<string>('createAt');
    const [sortType, setSortType] = useState<string>('ASC');
    const [loading, setLoading] = useState<boolean>(false);

    const [remittanceData, getRemittanceData] = useState<any[]>([]);
    const [remittanceDate, setRemittanceDate] = useState<string>(momentNow);
    const [remittancePage, setRemittancePage] = useState<number>(1);
    const [remittanceLimitPage, setRemittanceLimitPage] = useState<number>(10);

    const [checkDF, setCheckDf] = useState<boolean>(false);
    const [checkCash, setCheckCash] = useState<boolean>(false);

    const [checkTreatmentRight, setCheckTreatmentRight] = useState<boolean>(false);


    const verifyPermission = async () => {
        const condition: FindAllReportInterface = {
            page: 1, pageLimit: 200
        }
        ReportApi.reportList(condition)
            .then(({ data }) => {
                const check = _.find(data, (val: any) => String(val.reportKey) === 'REMITTANCE') ? true : false
                if (!check) {
                    history.push(routeName.report)
                    return false
                }
            })

        ClinicApi.findAllBranches()
            .then(({ data }) => setBranches(data))
            .catch((e) => { return })

        return true
    }

    useEffect(() => {
        verifyPermission()
    }, [])

    const fetchRemittance = async () => {
        setLoading(true)
        try {
            let condition: any = {
                page: remittancePage, pageLimit: remittanceLimitPage,
                branchId: branchId, date: remittanceDate
            }
            if (sortType) condition = { ...condition, sortType: sortType }
            if (sortBy) condition = { ...condition, sortBy: sortBy }
            if (checkDF) condition = { ...condition, df: checkDF === true ? 1 : 0 }
            if (checkTreatmentRight) condition = { ...condition, treatmentRight: checkTreatmentRight === true ? 1 : 0 }
            if (remittanceDate !== null)
                condition = { ...condition, date: remittanceDate }
            const { headers, data } = await ReportApi.reportRemittance(condition)
            getRemittanceData(data)
            setRowCount(Number(headers['x-total'] || 0))
        } catch (err) {
            notiError(t('ERROR'))
            setLoading(false)
        }
        finally {
            setLoading(false)
        }
    }

    /** UseEffect */
    useEffect(() => {
        fetchRemittance()
    }, [remittancePage, remittanceLimitPage, sortBy, sortType])

    const filter = [
        branchId === 0 ? `${t(`REPORT.FILTER.ALL`)}` : `${_.get(_.find(branches, { branchId: branchId }), 'branchName') || _.get(_.find(branches, { branchId: branchId }), 'branchNameEn')}`,
        remittanceDate
    ]


    const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
        setSortType(sortTypeVal)
        setSortBy(sortByVal)
    }

    const handleChangeRowsPerPage = (limit: number) => setRemittanceLimitPage(limit)

    const handleChangePage = (val: number) => setRemittancePage(val)

    const onExport = async () => {
        if (!remittanceData) return
        const report: any = remittanceData
        report.branchName = filter[0] || ''
        report.date = filter[1] || ''
        report.t = t
        report.treatmentRight = checkTreatmentRight
        report.df = checkDF
        report.cash = checkCash
        await ExportToExcel(report)
    }

    return (
        <Box>
            <HeaderCard text={t('REPORT.REPORT_NAME.REMITTANCE')} />
            <Box px={3} pt={2}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <SelectInput
                            label={t('REPORT.FILTER.BRANCH')}
                            value={branchId}
                            onChange={e => setBranchId(Number(e.target.value))}
                            options={[
                                ...branches.map(b => ({ value: b.branchId, name: b.branchName || b.branchNameEn }))
                            ]}
                            width="100%"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <InputSecondNewDatePicker
                            dateFormat={"DD/MM/YYYY"}
                            placeholder={t('REPORT.FILTER.DATE')}
                            label={''}
                            inputHeight={42}
                            value={remittanceDate}
                            onchange={(e: any) => {
                                setRemittanceDate(moment(e).format('YYYY-MM-DD'))
                                setRemittancePage(1)
                            }}
                            views={PICKER_VIEWS.DAY || PICKER_VIEWS.MONTH || PICKER_VIEWS.YEAR}
                        />
                    </Grid>
                    <Grid item display={'flex'} flexDirection={'row'} gap={1} xs={12} sm={6} md={3} xl={3}>
                        <Grid >
                            <CheckboxButton
                                label={t('DF')}
                                checked={checkDF}
                                onChange={() => {
                                    setCheckDf(!checkDF)
                                    setRemittancePage(1)
                                }}
                            />
                        </Grid>
                        <Grid >
                            <CheckboxButton
                                label={t('REPORT.FILTER.CASH')}
                                checked={checkCash}
                                onChange={() => {
                                    setCheckCash(!checkCash)
                                    setRemittancePage(1)
                                }}
                            />
                        </Grid>
                        <Grid >
                            <CheckboxButton
                                label={t('REPORT.FILTER.TREATMENT')}
                                checked={checkTreatmentRight}
                                onChange={() => {
                                    setCheckTreatmentRight(!checkTreatmentRight)
                                    setRemittancePage(1)
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12} sm={12} md={3} xl={5}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                            <MainButton
                                title={t('INPUT.SEARCH')}
                                type="primary"
                                variant="contained"
                                onClick={fetchRemittance}
                                style={{ width: '120px' }}
                            />
                            <UseStyled.DropdownDocument>
                                <Dropdown.Toggle id="dropdown-basic" className='ml-xl-auto'>
                                    {t('PRINT_DOCUMENT')}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={onExport}>
                                        {t('Excel')}
                                    </Dropdown.Item>
                                    <FunctionPrint
                                        content={() => componentRef.current}

                                        removeIfram
                                        trigger={() => <Dropdown.Item >
                                            {t('PDF')}
                                        </Dropdown.Item>}
                                        style={`@page { size: 793.7007874px 1122.519685px;  margin: 0.5cm; } @media print { body { -webkit-print-color-adjust: exact; }`}
                                    />
                                </Dropdown.Menu>
                            </UseStyled.DropdownDocument>
                        </Box>
                    </Grid>
                </Grid>
                <Box className={'mt-3 pl-xl- pr-xl-2 pb-4'}>
                    <RemittanceList
                        page={remittancePage}
                        pageLimit={remittanceLimitPage}
                        rowCount={rowCount}
                        sortBy={sortBy}
                        sortType={sortType}
                        data={remittanceData}
                        onRequestSort={onRequestSort}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                        handleChangePage={handleChangePage}
                        componentRef={componentRef}
                        cash={checkCash}
                        treatmentRight={checkTreatmentRight}
                        df={checkDF}
                        filter={filter}
                    />
                </Box>
            </Box>
            <Loading show={loading} type="fullLoading" />
        </Box >
    )
}